import './NotificationOverlay.scss';

import React, { useEffect, useMemo, useState } from 'react';
import { useNotifications } from '@go-client/context/NotificationsContext';
import { BubbleButton, BubbleProgress } from './BubbleUI';
import { BareUserNotification } from '@go-shared/types/userNotification';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { ObjectID } from 'bson';
import uuid from 'uuid';
import { getLocaleDateStringLocal, startOfDayLocal } from '@go-shared/lib/time';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface NotifBlock {
  key: string;
  refersTo: string[];
  notification: BareUserNotification;
  dismissed: boolean;
  autoDismiss?: boolean;
}

export default function NotificationOverlay(props: React.HTMLAttributes<HTMLDivElement>) {
  const { children, ...rest } = props;

  const { user } = useUserInfo();
  const { notifications: allNotifs } = useNotifications();

  const [notifBlocks, setNotifBlocks] = useState<NotifBlock[] | undefined>(undefined);

  const notifications = useMemo(() => allNotifs.filter(n => n.tier === 2), [allNotifs]);

  useEffect(() => {
    if (notifications.length) {
      setNotifBlocks([
        {
          key: uuid(),
          dismissed: false,
          autoDismiss: true,
          refersTo: notifications.map(n => n._id),
          notification: {
            _id: new ObjectID().toHexString(),
            user: user._id,
            title: `You Have ${notifications.length} Pending Alerts`,
            description: `Click on the bell icon at the top to see your alerts`,
            relevance: 'info',
            status: 'new',
            tier: 2,
            category: 'basic',
            createdAt: getLocaleDateStringLocal(startOfDayLocal()),
            updatedAt: getLocaleDateStringLocal(startOfDayLocal()),
          },
        },
      ]);
    } else {
      setNotifBlocks([]);
    }
  }, []);

  useEffect(() => {
    if (notifBlocks === undefined) return;

    const newNotifBlocks = notifications
      .filter(n => !notifBlocks.find(nb => nb.refersTo.includes(n._id)))
      .map(n => ({
        key: n._id,
        refersTo: [n._id],
        dismissed: false,
        autoDismiss: true,
        notification: n,
      }));
    setNotifBlocks([...notifBlocks, ...newNotifBlocks]);
  }, [notifications]);

  function dismissNotifBlock(block: NotifBlock) {
    if (!notifBlocks) return;

    const indx = notifBlocks.findIndex(nb => nb.key === block.key);

    if (indx < 0) return;

    const newBlocks = [...notifBlocks];

    newBlocks.splice(indx, 1, {
      ...newBlocks[indx],
      dismissed: true,
    });

    setNotifBlocks(newBlocks);
  }

  const visibleNotifBlocks = (notifBlocks || []).filter(nb => !nb.dismissed);

  return (
    <>
      {children}
      <div className="go-notifications mb-n3" {...rest}>
        {visibleNotifBlocks.map(block => (
          <div
            key={block.key}
            className={`notif-outer w-full pl-2 mb-3 rounded-xl shadow-xl
            ${block.notification.relevance}`}
          >
            <div className="notif-inner p-5 rounded-xl">
              <div className="notif-title mb-3 flex items-center">
                <FontAwesomeIcon icon="bell" className="mr-2" />
                <span className="uppercase font-bold mr-2">{block.notification.title}</span>
                <BubbleButton
                  inverse
                  className="ml-auto -mr-3 -mt-3"
                  variant="navy"
                  onClick={() => dismissNotifBlock(block)}
                  icon="xmark-large"
                />
              </div>
              <div
                className="notif-description"
                dangerouslySetInnerHTML={{ __html: block.notification.description }}
              />
              {block.autoDismiss && (
                <BubbleProgress className="mt-3" onFinish={() => dismissNotifBlock(block)} />
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
